import { motion } from "motion/react";
import { forwardRef } from "react";

/**
 * "Unread messages" divider — a hairline rule with a centered label,
 * marking where the visitor's unread run begins. The tab scrolls this into
 * the centre of the viewport on open so the visitor lands on the first
 * thing they haven't seen. Forwards a ref so the tab can drive that scroll.
 *
 * It's a `motion` element so that when the tab clears the anchor (the ~4 s
 * auto-dismiss — "show the marker briefly, then let the thread read as
 * normal"), `AnimatePresence` fades + collapses it out instead of a hard
 * pop. Enter is instant (`initial={false}`-friendly) so it doesn't animate
 * in under the open-scroll that centres it.
 */
export const UnreadDivider = forwardRef<HTMLDivElement, { label: string }>(({ label }, ref) => (
  <motion.div
    ref={ref}
    className="flex items-center gap-2 py-1 overflow-hidden"
    initial={{ opacity: 1 }}
    exit={{ opacity: 0, height: 0, paddingTop: 0, paddingBottom: 0 }}
    transition={{ duration: 0.18, ease: "easeOut" }}
  >
    <span aria-hidden="true" className="h-px flex-1 bg-wx-danger/40" />
    <span className="text-[11px] font-semibold text-wx-danger">{label}</span>
    <span aria-hidden="true" className="h-px flex-1 bg-wx-danger/40" />
  </motion.div>
));
UnreadDivider.displayName = "UnreadDivider";
